import { LogService } from "@/services/logging";
import { IUpdatedPrice } from "@/types/db/timeslots";
import {
  GetSyncedPricesQuery,
  GetSyncedPricesQueryVariables,
} from "@/types/gql/generated/graphql";
import { apolloClient } from "@/utils/graphql-client";
import dayjs from "dayjs";
import { GET_SyncedPrices } from "./queries/price.graphql";

export async function getUpdatedRetailPrices(
  ids: number[],
  timeout = 1000
): Promise<IUpdatedPrice[]> {
  LogService.info(`Availability: Sending request to synch retail prices`, {
    params: {
      ids,
      timeout,
    },
  });

  const abortController = new AbortController();
  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, timeout);

  try {
    const res = await apolloClient.query<
      GetSyncedPricesQuery,
      GetSyncedPricesQueryVariables
    >({
      query: GET_SyncedPrices,
      variables: {
        condition: `(id,in,${ids.join(",")})`,
      },
      context: {
        fetchOptions: {
          signal: abortController.signal,
        },
      },
      errorPolicy: "all",
      fetchPolicy: "network-only",
    });

    clearTimeout(timeoutId);
    return res.data.prices as IUpdatedPrice[];
  } catch (error) {
    clearTimeout(timeoutId);

    // handle abort/timeout errors
    if (abortController.signal.aborted) {
      LogService.info(
        `Availability: GetSyncedPrices query was aborted/timed out after ${timeout}ms`,
        {
          params: {
            ids,
          },
        }
      );
      throw new Error(`GetSyncedPrices query timed out after ${timeout}ms`);
    }

    // handle other errors
    LogService.info(
      `Availability: GetSyncedPrices - Failed to fetch retail prices`,
      {
        params: {
          ids,
        },
        error,
      }
    );

    throw error;
  }
}
